import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useOffline } from '../../context/OfflineContext';
import { useToast } from '../../context/ToastContext';
import { 
  createEvent, 
  updateEvent, 
  deleteEvent, 
  createExpense,
  generateTempId,
} from '../../services/syncService';
import { attachmentsApi } from '../../services/api';
import { uploadPendingAttachments } from '../../services/attachmentCache';
import { mapEventToExpenseCategory } from '../../utils/categories';
import { parseISOForForm, createISODateTime } from '../../utils/timezone';
import { validateEventForm, validateExpenseForm } from '../../utils/validation';
import { useFocusTrap, useEscapeKey } from '../../utils/accessibility';
import ConfirmModal from '../UI/ConfirmModal';
import EntryTypeSelector from './EntryTypeSelector';
import EventForm from './EventForm';
import ExpenseForm from './ExpenseForm';

/**
 * EntryModal - Modal for creating/editing events and expenses
 * Orchestrates the form state and submission logic
 */
export default function EntryModal({ isOpen, onClose, onSave, event, defaultDate, trips = [], defaultTripId = null }) {
  const { user } = useAuth();
  const { isOnline, updatePendingCount } = useOffline();
  const toast = useToast();
  const isEditing = !!event;
  const modalRef = useRef(null);
  
  // Accessibility: trap focus and handle escape key
  useFocusTrap(isOpen, modalRef);
  useEscapeKey(isOpen, onClose);
  
  // Entry type: 'event', 'expense', or 'both'
  const [entryType, setEntryType] = useState('event');
  
  // Event fields
  const [eventData, setEventData] = useState(getInitialEventData());
  const [showTravelDetails, setShowTravelDetails] = useState(false);
  
  // Expense fields
  const [expenseData, setExpenseData] = useState(getInitialExpenseData());
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Attachments state (for existing events)
  const [attachments, setAttachments] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  
  // Pending attachments state (for new events)
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const tempIdRef = useRef(null);

  // Derived state
  const needsSeparateTimezones = eventData.category === 'flight' || eventData.category === 'transit';

  function getInitialEventData(tz = 'Europe/Dublin', tripId = null) {
    return {
      title: '',
      description: '',
      category: 'activity',
      startDate: '',
      startTime: '09:00',
      endDate: '',
      endTime: '10:00',
      departure_timezone: tz,
      arrival_timezone: tz,
      location: '',
      notes: '',
      flight_number: '',
      terminal: '',
      check_in_info: '',
      luggage_allowance: '',
      booking_reference: '',
      trip_id: tripId,
    };
  }

  function getInitialExpenseData(currency = 'EUR') {
    return {
      description: '',
      category: 'miscellaneous',
      amount: '',
      currency: currency,
      is_planned: true,
      expense_date: '',
    };
  }

  // Initialize form when modal opens
  useEffect(() => {
    if (!isOpen) return;

    if (event) {
      // Editing existing event
      initializeForEditing();
    } else {
      // New entry
      initializeForCreating();
    }
    setError(null);
    setValidationErrors({});
  }, [isOpen, event, defaultDate, user]);

  function initializeForEditing() {
    setEntryType('event');
    const startParts = parseISOForForm(event.start_datetime, event.local_timezone);
    const endParts = parseISOForForm(event.end_datetime, event.arrival_timezone || event.local_timezone);
    
    setEventData({
      title: event.title || '',
      description: event.description || '',
      category: event.category || 'activity',
      startDate: startParts.date,
      startTime: startParts.time,
      endDate: endParts.date,
      endTime: endParts.time,
      departure_timezone: event.local_timezone || 'Europe/Dublin',
      arrival_timezone: event.arrival_timezone || event.local_timezone || 'Europe/Dublin',
      location: event.location || '',
      notes: event.notes || '',
      flight_number: event.flight_number || '',
      terminal: event.terminal || '',
      check_in_info: event.check_in_info || '',
      luggage_allowance: event.luggage_allowance || '',
      booking_reference: event.booking_reference || '',
      trip_id: event.trip_id || null,
    });
    
    const hasTravelDetails = event.flight_number || event.terminal || event.check_in_info || 
                             event.luggage_allowance || event.booking_reference;
    setShowTravelDetails(!!hasTravelDetails);
    
    // Load attachments
    setAttachments([]);
    setShowAttachments(event.attachment_count > 0);
    if (event.id && isOnline) {
      setLoadingAttachments(true);
      attachmentsApi.list({ event_id: event.id })
        .then(data => {
          setAttachments(data);
          setShowAttachments(data.length > 0);
        })
        .catch(err => console.error('Failed to load attachments:', err))
        .finally(() => setLoadingAttachments(false));
    }
  }

  function initializeForCreating() {
    setEntryType('event');
    const dateStr = defaultDate ? format(defaultDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
    const tz = user?.home_timezone || 'Europe/Dublin';
    
    setEventData({
      ...getInitialEventData(tz, defaultTripId),
      startDate: dateStr,
      endDate: dateStr,
    });
    setShowTravelDetails(false);
    
    setAttachments([]);
    setShowAttachments(false);
    
    tempIdRef.current = generateTempId();
    setPendingAttachments([]);
    setUploadError(null);
    
    setExpenseData({
      ...getInitialExpenseData(user?.base_currency || 'EUR'),
      expense_date: dateStr,
    });
  }

  // Auto-map expense category when event category changes
  useEffect(() => {
    if (entryType === 'both') {
      setExpenseData(prev => ({
        ...prev,
        category: mapEventToExpenseCategory(eventData.category),
        description: eventData.title,
        expense_date: eventData.startDate,
      }));
    }
  }, [eventData.category, eventData.title, eventData.startDate, entryType]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    
    // Run validation
    let allErrors = {};
    
    if (entryType === 'event' || entryType === 'both') {
      const eventValidation = validateEventForm(eventData);
      if (!eventValidation.isValid) {
        allErrors = { ...allErrors, ...eventValidation.errors };
      }
    }
    
    if (entryType === 'expense' || entryType === 'both') {
      const expenseValidation = validateExpenseForm(expenseData, entryType);
      if (!expenseValidation.isValid) {
        // Prefix expense errors to avoid conflicts with event errors
        Object.entries(expenseValidation.errors).forEach(([key, value]) => {
          allErrors[`expense_${key}`] = value;
        });
      }
    }
    
    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      setError('Please fix the errors above');
      return;
    }
    
    setValidationErrors({});
    setLoading(true);

    try {
      let createdEvent = null;

      // Create/update event if needed
      if (entryType === 'event' || entryType === 'both') {
        createdEvent = await submitEvent();
      }

      // Create expense if needed
      if (entryType === 'expense' || entryType === 'both') {
        await submitExpense(createdEvent?.id);
      }

      // Update pending count if offline
      if (!isOnline && updatePendingCount) {
        updatePendingCount();
      }

      // Show success toast
      const action = isEditing ? 'updated' : 'created';
      const itemType = entryType === 'both' ? 'Event and expense' : entryType === 'event' ? 'Event' : 'Expense';
      toast.success(`${itemType} ${action} successfully`);

      onSave();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error(err.message || 'Failed to save entry');
    } finally {
      setLoading(false);
    }
  }

  async function submitEvent() {
    const startDatetimeUTC = createISODateTime(
      eventData.startDate, 
      eventData.startTime, 
      eventData.departure_timezone
    );
    
    const endTimezone = needsSeparateTimezones 
      ? eventData.arrival_timezone 
      : eventData.departure_timezone;
    const endDatetimeUTC = createISODateTime(
      eventData.endDate, 
      eventData.endTime, 
      endTimezone
    );

    const eventPayload = {
      title: eventData.title,
      description: eventData.description || null,
      category: eventData.category,
      start_datetime: startDatetimeUTC,
      end_datetime: endDatetimeUTC,
      local_timezone: eventData.departure_timezone,
      arrival_timezone: needsSeparateTimezones ? eventData.arrival_timezone : eventData.departure_timezone,
      location: eventData.location || null,
      notes: eventData.notes || null,
      flight_number: needsSeparateTimezones ? (eventData.flight_number || null) : null,
      terminal: needsSeparateTimezones ? (eventData.terminal || null) : null,
      check_in_info: needsSeparateTimezones ? (eventData.check_in_info || null) : null,
      luggage_allowance: needsSeparateTimezones ? (eventData.luggage_allowance || null) : null,
      booking_reference: needsSeparateTimezones ? (eventData.booking_reference || null) : null,
      trip_id: eventData.trip_id || null,
    };

    let result;
    if (isEditing) {
      result = await updateEvent(event.id, eventPayload, isOnline);
    } else {
      result = await createEvent(eventPayload, isOnline, tempIdRef.current);
      
      // Upload pending attachments for new event
      if (pendingAttachments.length > 0 && result?.id && isOnline) {
        console.log(`[EntryModal] Uploading ${pendingAttachments.length} pending attachments`);
        const uploadResult = await uploadPendingAttachments(
          tempIdRef.current,
          result.id,
          'event',
          attachmentsApi
        );
        
        if (uploadResult.failed > 0) {
          setUploadError(`${uploadResult.failed} attachment(s) failed to upload: ${uploadResult.errors.join(', ')}`);
        }
      } else if (pendingAttachments.length > 0 && !isOnline) {
        console.log('[EntryModal] Offline - pending attachments will upload on sync');
      }
    }
    
    return result;
  }

  async function submitExpense(eventId) {
    const expensePayload = {
      description: expenseData.description || eventData.title,
      category: expenseData.category,
      amount: parseFloat(expenseData.amount),
      currency: expenseData.currency,
      is_planned: expenseData.is_planned,
      expense_date: expenseData.expense_date || eventData.startDate,
      event_id: eventId || null,
    };

    await createExpense(expensePayload, isOnline);
  }

  async function handleDelete() {
    if (!event) return;
    
    setLoading(true);
    try {
      await deleteEvent(event.id, isOnline);
      
      if (!isOnline && updatePendingCount) {
        updatePendingCount();
      }
      
      toast.success('Event deleted successfully');
      onSave();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="entry-modal-title"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          ref={modalRef}
          className="relative rounded-lg shadow-2xl w-full max-w-lg animate-scale-in"
          style={{ backgroundColor: 'var(--color-bg-canvas)' }}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between px-6 py-4"
            style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
          >
            <h2 
              id="entry-modal-title"
              className="text-xl font-display font-semibold" 
              style={{ color: 'var(--color-text-primary)' }}
            >
              {isEditing ? 'Edit Event' : 'New Entry'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-sm transition-colors"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label="Close modal"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Entry Type Selector (only for new entries) */}
              {!isEditing && (
                <EntryTypeSelector 
                  value={entryType} 
                  onChange={setEntryType} 
                />
              )}

              {/* Event Fields */}
              {(entryType === 'event' || entryType === 'both') && (
                <EventForm
                  eventData={eventData}
                  onChange={(data) => {
                    setEventData(data);
                    // Clear validation errors for changed fields
                    if (Object.keys(validationErrors).length > 0) {
                      setValidationErrors({});
                    }
                  }}
                  errors={validationErrors}
                  needsSeparateTimezones={needsSeparateTimezones}
                  showTravelDetails={showTravelDetails}
                  onToggleTravelDetails={() => setShowTravelDetails(!showTravelDetails)}
                  isEditing={isEditing}
                  showAttachments={showAttachments}
                  onToggleAttachments={() => setShowAttachments(!showAttachments)}
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  eventId={event?.id}
                  loadingAttachments={loadingAttachments}
                  pendingAttachments={pendingAttachments}
                  onPendingChange={setPendingAttachments}
                  tempId={tempIdRef.current}
                  uploadError={uploadError}
                  trips={trips}
                  disabled={loading}
                />
              )}

              {/* Expense Fields */}
              {(entryType === 'expense' || entryType === 'both') && (
                <ExpenseForm
                  expenseData={expenseData}
                  onChange={(data) => {
                    setExpenseData(data);
                    if (Object.keys(validationErrors).length > 0) {
                      setValidationErrors({});
                    }
                  }}
                  errors={{
                    description: validationErrors.expense_description,
                    category: validationErrors.expense_category,
                    amount: validationErrors.expense_amount,
                    currency: validationErrors.expense_currency,
                    expense_date: validationErrors.expense_expense_date,
                  }}
                  entryType={entryType}
                  showDivider={entryType === 'both'}
                  disabled={loading}
                />
              )}

              {/* Error message */}
              {error && (
                <div 
                  className="p-3 rounded-sm text-sm"
                  style={{ 
                    backgroundColor: 'rgba(193, 96, 92, 0.1)',
                    color: 'var(--color-status-error)',
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div 
              className="flex items-center justify-between px-6 py-4 rounded-b-lg"
              style={{ 
                borderTop: '1px solid var(--color-border-subtle)',
                backgroundColor: 'var(--color-bg-surface)',
              }}
            >
              {isEditing ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                  className="btn-danger btn-sm"
                >
                  Delete
                </button>
              ) : (
                <div />
              )}
              
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Saving...' : isEditing ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Event"
        message={`Are you sure you want to delete "${eventData.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
